import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Night mode preference. Stored per device; public/index.html applies it
// before React boots so the first paint is already in the right theme.
// Keep STORAGE_KEY and the resolve logic in sync with that inline script.
export const STORAGE_KEY = 'theme';
export const THEME_OPTIONS = ['light', 'dark', 'system'];
const DEFAULT_PREFERENCE = 'light';

const META_COLORS = { light: '#ffffff', dark: '#0f1520' };

const darkQuery = () =>
  (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

export function readPreference() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return THEME_OPTIONS.includes(v) ? v : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

export function resolveTheme(preference, systemDark) {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference === 'dark' ? 'dark' : 'light';
}

function applyTheme(theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_COLORS[theme]);
}

const ThemeContext = createContext({
  preference: DEFAULT_PREFERENCE,
  theme: 'light',
  setPreference: () => {},
});

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readPreference);
  const [systemDark, setSystemDark] = useState(() => !!darkQuery()?.matches);

  const theme = resolveTheme(preference, systemDark);

  useEffect(() => { applyTheme(theme); }, [theme]);

  // Follow the OS while on "system".
  useEffect(() => {
    const mq = darkQuery();
    if (!mq) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Keep every open tab on the same theme.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPreferenceState(readPreference());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPreference = useCallback((next) => {
    if (!THEME_OPTIONS.includes(next)) return;
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    setPreferenceState(next);
  }, []);

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);

// The full logo has dark "GAIN" lettering; night mode uses the white-lettered
// variant (same size, transparent background).
export const useBrandLogo = () => (useTheme().theme === 'dark' ? '/logo-white.png' : '/logo.png');
